import { useEffect, useState } from "react";

import { createInvite, sendInviteEmail } from "src/api/client";

interface Props {
  token: string;
  onClose: () => void;
  onError: (message: string) => void;
  onEmailSent: () => void;
  initialPatientId?: string;
  autoSubmit?: boolean;
}

const ERROR_COPY: Record<string, string> = {
  invalid_request: "Enter a valid Athena patient ID.",
  patient_not_found:
    "We couldn't find a patient with that Athena ID. Double-check the ID and try again.",
  state_not_supported:
    "This patient's state isn't covered by the walk-in clinic yet, so they can't be invited right now. Check the patient's non-statement address state field in Athena.",
};

function errorCopyFor(code: string): string {
  return ERROR_COPY[code] ?? "Something went wrong sending the invite. Please try again.";
}

export function InviteModal({
  token,
  onClose,
  onError,
  onEmailSent,
  initialPatientId,
  autoSubmit,
}: Props) {
  const [patientId, setPatientId] = useState(initialPatientId ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [shortCode, setShortCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Runs once on mount to auto-submit a deep-linked patient ID.
  useEffect(() => {
    if (autoSubmit && initialPatientId) void handleSubmit();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit() {
    if (!/^\d+$/.test(patientId.trim())) {
      setError(errorCopyFor("invalid_request"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const invite = await createInvite(token, patientId.trim());
      setLink(invite.link);
      setShortCode(invite.short_code);
    } catch (err) {
      setError(errorCopyFor((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  async function handleCopy() {
    if (!link) return;
    await navigator.clipboard.writeText(`Join our virtual waiting room: ${link}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleSendEmail() {
    if (!shortCode) return;
    setSendingEmail(true);
    try {
      await sendInviteEmail(token, shortCode);
      onEmailSent();
    } catch {
      onError("Something went wrong sending the email. Please try again.");
    } finally {
      setSendingEmail(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="modal-title">Invite a Member</h3>

        {link ? (
          <>
            <label className="field-label" htmlFor="invite-link">
              Link
            </label>
            <div className="invite-link-row">
              <input
                id="invite-link"
                className="field-input"
                value={`Join our virtual waiting room: ${link}`}
                readOnly
              />
              <button className="btn btn-outline" onClick={() => void handleCopy()}>
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="invite-link-guidance">
              This link doesn't expire, and it's safe to send again if the patient needs it.
            </p>
            <hr className="modal-divider" />
            <p className="invite-email-description">
              Send link to patient's email registered in Athena.
            </p>
            <button
              className="btn btn-outline"
              onClick={() => void handleSendEmail()}
              disabled={sendingEmail}
            >
              {sendingEmail ? "Sending…" : "Send by email"}
            </button>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <p>Enter the patient's Athena patient ID. We'll look them up and generate a link.</p>
            <label className="field-label" htmlFor="invite-patient-id">
              Athena Patient ID
            </label>
            <input
              id="invite-patient-id"
              className="field-input"
              placeholder="123456"
              inputMode="numeric"
              value={patientId}
              onChange={(e) => setPatientId(e.target.value)}
              disabled={busy}
            />
            {error && <p className="modal-error">{error}</p>}
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => void handleSubmit()}
                disabled={!patientId.trim() || busy}
              >
                {busy ? "Sending…" : "Get Link"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
